import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Comment,
  CreativeCopyChange,
  CreativeCopyEditEvent,
  User,
} from '@marketing-milk/interfaces'
import { formatDistance } from 'date-fns'
import { useComments } from 'hooks/api/useComments'
import { useCreativeCopyEdits } from 'hooks/api/useCreativeCopyEdits'
import { dark, radius, type as typo } from '../tokens'

interface Props {
  businessID: number
  creativeID: number
  /**
   * A comment the reviewer arrived to read. Scrolled to and highlighted once the
   * thread has actually loaded — the row it names doesn't exist before then.
   */
  targetCommentID?: number | null
}

const HIGHLIGHT_MS = 2000

/** One thing that happened to the ad, whichever source it came from. */
type FeedEntry =
  | { kind: 'comment'; key: string; createdAt: string; comment: Comment }
  | { kind: 'edit'; key: string; createdAt: string; edit: CreativeCopyEditEvent }

const authorName = (author: User | null): string =>
  author ? `${author.firstName} ${author.lastName}` : 'Deleted user'

const formatRelative = (iso: string) =>
  formatDistance(new Date(iso), new Date(), { addSuffix: true })

const AD_FIELD_LABELS: Record<string, string> = {
  primaryText: 'Primary text',
  headline: 'Headline',
  description: 'Description',
  callToAction: 'Call to action',
  url: 'Website URL',
  name: 'Creative name',
  graphicText: 'Graphic text',
  displayLink: 'Display URL',
  callPhoneNumber: 'Phone number',
  leadCard: 'Lead card',
}

const SLIDE_FIELD_LABELS: Record<string, string> = {
  headline: 'Headline',
  description: 'Description',
  url: 'Website URL',
}

const MEDIA_ACTIONS: Record<'add' | 'remove', string> = { add: 'added', remove: 'removed' }

const POOL_LABELS: Record<string, string> = {
  primaryTexts: 'Primary texts pool',
  headlines: 'Headlines pool',
  descriptions: 'Descriptions pool',
  callToActions: 'Calls to action pool',
}

/**
 * What a change is called in the row. A carousel card and a dynamic pool carry
 * their identity in the label: on a five-card ad, "edited Headline" decides
 * nothing.
 */
const changeLabel = (change: CreativeCopyChange): string => {
  if (change.scope === 'slide') {
    return `${SLIDE_FIELD_LABELS[change.field] ?? change.field} · Card ${change.position}`
  }
  if (change.scope === 'pool') return POOL_LABELS[change.pool] ?? change.pool
  if (change.scope === 'media') {
    // A card carries its number: on a five-card ad, "Media" decides nothing. A
    // replacement needs no verb — the before → after line below says it.
    const subject = change.where === 'card' ? `Media · Card ${change.position}` : 'Media'
    return change.action === 'replace' ? subject : `${subject} · ${MEDIA_ACTIONS[change.action]}`
  }
  return AD_FIELD_LABELS[change.field] ?? change.field
}

/** Nothing renders as nothing otherwise, and "the headline was cleared" is the point. */
const displayValue = (value: string | string[] | null): string => {
  if (Array.isArray(value)) return value.length ? value.join(' · ') : 'Blank'
  return value === null || value === '' ? 'Blank' : value
}

const summarise = (changes: CreativeCopyChange[]): string => {
  const labels = changes.map(changeLabel).filter((label, i, all) => all.indexOf(label) === i)
  if (labels.length === 0) return 'Edited the copy'
  if (labels.length === 1) return `Edited ${labels[0]}`
  return `Edited ${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`
}

/**
 * The ad's activity — approvals, rejections with the client's reason, the
 * comments people left, and the copy edits that answered them — in the order it
 * happened. Read-only: comments are written in the packet's comment drawer, so this column
 * can't become a second feedback channel with its own unresolved counts,
 * mentions and Slack delivery.
 *
 * Deliberately not `CommentThread`: that is an overlaid drawer that navigates
 * and rewrites the URL when it closes, which is wrong content for a 360px grid
 * cell beside the copy the reviewer is editing.
 *
 * Verdict comments are shown as written. They are best-effort and editable, so
 * this reads as activity — the review row and its status pill remain the record
 * of where the ad actually stands. A copy edit is the opposite: an append-only
 * record nobody can revise, which is why it is a separate source.
 */
export function ReviewHistoryFeed({ businessID, creativeID, targetCommentID }: Props) {
  const {
    comments,
    isLoading: commentsLoading,
    isError: commentsError,
  } = useComments(businessID, 'creative', String(creativeID))
  const {
    edits,
    isLoading: editsLoading,
    isError: editsError,
  } = useCreativeCopyEdits(businessID, creativeID)
  const rowRefs = useRef<Record<number, HTMLLIElement | null>>({})
  const [highlighted, setHighlighted] = useState<number | null>(null)

  const targetLoaded =
    typeof targetCommentID === 'number' && comments.some(c => c.id === targetCommentID)

  useEffect(() => {
    if (!targetLoaded || typeof targetCommentID !== 'number') return
    rowRefs.current[targetCommentID]?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setHighlighted(targetCommentID)
    const timer = setTimeout(() => setHighlighted(null), HIGHLIGHT_MS)
    return () => clearTimeout(timer)
  }, [targetLoaded, targetCommentID])

  // Comments arrive oldest-first and edits newest-first, so they are merged and
  // sorted here. The sort is stable, which is what keeps a reply directly under
  // the comment it answers when the two share a timestamp.
  const entries: FeedEntry[] = useMemo(() => {
    const merged: FeedEntry[] = [
      ...comments.map(
        (comment): FeedEntry => ({
          kind: 'comment',
          key: `comment-${comment.id}`,
          createdAt: comment.createdAt,
          comment,
        })
      ),
      ...[...edits].reverse().map(
        (edit): FeedEntry => ({
          kind: 'edit',
          key: `edit-${edit.id}`,
          createdAt: edit.createdAt,
          edit,
        })
      ),
    ]
    return merged.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
  }, [comments, edits])

  // Either source failing must not blank out the other: a readable comment
  // thread is worth more than a complete one.
  if (commentsLoading || editsLoading) return <Note>Loading history…</Note>
  if (commentsError && editsError) return <Note>History couldn’t be loaded.</Note>
  if (entries.length === 0) return <Note>Nothing has happened to this ad yet.</Note>

  return (
    <>
      {commentsError && <Note>Comments couldn’t be loaded.</Note>}
      {editsError && <Note>Copy edits couldn’t be loaded.</Note>}
      <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
        {entries.map(entry =>
          entry.kind === 'comment' ? (
            <Row
              key={entry.key}
              author={authorName(entry.comment.author)}
              createdAt={entry.createdAt}
              indented={
                entry.comment.parentCommentID !== null &&
                entry.comment.parentCommentID !== undefined
              }
              highlighted={highlighted === entry.comment.id}
              rowRef={el => {
                rowRefs.current[entry.comment.id] = el
              }}
            >
              <div
                style={{
                  marginTop: 4,
                  color: dark.textMuted,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {entry.comment.body}
              </div>
            </Row>
          ) : (
            <Row
              key={entry.key}
              author={authorName(entry.edit.author)}
              createdAt={entry.createdAt}
              indented={false}
              highlighted={false}
            >
              <CopyEditBody changes={entry.edit.changes} />
            </Row>
          )
        )}
      </ul>
    </>
  )
}

/**
 * The row chrome both kinds share — who, when, and the highlight border the
 * deep-linked comment gets. Only the body differs.
 */
function Row({
  author,
  createdAt,
  indented,
  highlighted,
  rowRef,
  children,
}: {
  author: string
  createdAt: string
  indented: boolean
  highlighted: boolean
  rowRef?: (el: HTMLLIElement | null) => void
  children: React.ReactNode
}) {
  return (
    <li
      ref={rowRef}
      style={{
        marginBottom: 8,
        marginLeft: indented ? 16 : 0,
        padding: '8px 10px',
        borderRadius: radius.md,
        background: dark.surface,
        border: `1px solid ${highlighted ? dark.accent : dark.hairline}`,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <span
          style={{
            fontWeight: typo.weight.semibold,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {author}
        </span>
        <span
          style={{
            fontSize: typo.size.eyebrow,
            color: dark.textFaint,
            whiteSpace: 'nowrap',
          }}
        >
          {formatRelative(createdAt)}
        </span>
      </div>
      {children}
    </li>
  )
}

/**
 * A collapsed line naming the fields, expanding to each one's before and after.
 *
 * `<details>` rather than local disclosure state: the column is 360px beside the
 * copy fields, a dynamic-pool edit is ten strings, and the diff is what you want
 * on one row out of ten.
 */
function CopyEditBody({ changes }: { changes: CreativeCopyChange[] }) {
  return (
    <details style={{ marginTop: 4, color: dark.textMuted }}>
      <summary style={{ cursor: 'pointer' }}>{summarise(changes)}</summary>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
        {changes.map((change, i) => (
          <div key={i} style={{ wordBreak: 'break-word' }}>
            <div
              style={{
                fontSize: typo.size.eyebrow,
                color: dark.textFaint,
              }}
            >
              {changeLabel(change)}
            </div>
            <div>
              <s>{displayValue(change.before)}</s> → {displayValue(change.after)}
            </div>
          </div>
        ))}
      </div>
    </details>
  )
}

const Note = ({ children }: { children: React.ReactNode }) => (
  <p style={{ margin: 0, color: dark.textMuted }}>{children}</p>
)
